import type {
  EngineAccountOrder,
  EngineAccountView,
  EngineSignInMethod,
  PlanEngine,
  StandingStatus,
  StandingWindow,
} from "@bash/shared";
import { getRequiredAccessToken } from "./client";
import { apiUrl, responseSnippet } from "./base";

/**
 * Settings → Engine (the user's own plan). A dedicated fetch, like reach.ts: the
 * 404 of a dark flag or a user outside the cohort is a TYPED outcome that hides
 * the section, and must stay distinguishable from a failing server without
 * matching any message text.
 *
 * The GET reads persisted columns on the server — it never wakes the box (see
 * engine-preference-routes.handler.ts), which is what lets Settings say
 * "Connected as …" about a sandbox that is asleep.
 */
export type Engine = "native" | PlanEngine;
export type EngineConnection = "connected" | "needs_login" | null;

/**
 * Where the person stands on their own plan, as the box last read it and the
 * server remembered it (engine_accounts). Null when there is no row yet. The
 * window and status shapes are @bash/shared's, the one author of them.
 */
export interface EnginePlan {
  tier: string | null;
  windows: StandingWindow[];
  standingStatus: StandingStatus | null;
  measuredAt: string | null;
}

/**
 * One plan engine as Settings shows it (the server's PlanView): its sign-in,
 * where its plan stands, and its accounts.
 */
export interface PlanView {
  status: EngineConnection;
  statusAt: string | null;
  account: string | null;
  plan: EnginePlan | null;
  /**
   * Several accounts (spec 2026-10-01): every account of this engine in the
   * person's order, while ENGINE_MULTI_ACCOUNT_V1 is on. Absent while it is
   * off, and then Settings shows today's single row.
   */
  accounts?: EngineAccountView[];
  /**
   * "Which account Bash uses first" (several accounts): 'soonest' or
   * 'my_order'. Beside `accounts`, and absent with them.
   */
  accountOrder?: EngineAccountOrder;
  /** How Settings may sign in to it; the first is the one Connect starts with. */
  signInMethods: readonly EngineSignInMethod[];
}

/**
 * Mirrors the server contract; defined here so web and server stay decoupled.
 * Per engine since T3 (spec 2026-10-06): `plans` has one entry per plan engine
 * enabled for this person, and `pickable` is what "Bash works on" offers. The
 * single fields are the legacy mirror of `plans.claude_code`, and all a server
 * before T3 sends: read the body through plansOf (lib/engine-plans.ts), never
 * through them.
 */
export interface EngineBody extends Partial<Omit<PlanView, "signInMethods">> {
  engine: Engine;
  pickable?: Engine[];
  plans?: Partial<Record<PlanEngine, PlanView>>;
}

export type EngineStatus =
  { available: false } | { available: true; settings: EngineBody };

const PATH = "/api/preferences/engine";

/**
 * Every engine request carries a deadline. A connection that dies silently
 * would otherwise hold a request open for minutes, and the Settings row with
 * it (a Cancel waiting on its answer, a check waiting on the stored truth).
 * Past the deadline the request rejects, and each caller decides what that
 * means for the row.
 */
export const ENGINE_REQUEST_TIMEOUT_MS = 20_000;
const deadline = () => AbortSignal.timeout(ENGINE_REQUEST_TIMEOUT_MS);

async function authHeader(): Promise<Record<string, string>> {
  const token = await getRequiredAccessToken();
  return { Authorization: `Bearer ${token}` };
}

/** GET. 404 (flag dark / outside the cohort) => the section renders nothing. */
export async function getEngineSettings(): Promise<EngineStatus> {
  const res = await fetch(apiUrl(PATH), {
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.status === 404) return { available: false };
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  return { available: true, settings: (await res.json()) as EngineBody };
}

/**
 * PATCH the pick. Any non-2xx throws — including the 404 of a flag flipped while
 * the page was open, which is a failed save like any other and must not read as
 * success.
 */
export async function updateEngine(engine: Engine): Promise<EngineBody> {
  const res = await fetch(apiUrl(PATH), {
    method: "PATCH",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    signal: deadline(),
    body: JSON.stringify({ engine }),
  });
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  return (await res.json()) as EngineBody;
}

/**
 * PATCH "Which account Bash uses first" (several accounts). One choice per
 * person, for every plan engine. Answers the fresh Settings body, so "In use"
 * is drawn from the server's pick under the saved choice. Any non-2xx throws.
 */
export async function updateAccountOrder(
  accountOrder: EngineAccountOrder
): Promise<EngineBody> {
  const res = await fetch(apiUrl(PATH), {
    method: "PATCH",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    signal: deadline(),
    body: JSON.stringify({ accountOrder }),
  });
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  return (await res.json()) as EngineBody;
}

/**
 * POST the sign-out of one plan engine to a LIVE box. A sleeping one answers
 * 409 box_asleep, which is a STATE the section reports in plain words, not a
 * failure — read as a structured field, never from the message.
 */
export async function disconnectEngine(
  engine: PlanEngine
): Promise<"sent" | "box_asleep"> {
  const query = new URLSearchParams({ engine });
  const res = await fetch(apiUrl(`${PATH}/disconnect?${query}`), {
    method: "POST",
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.status === 202) return "sent";
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === "box_asleep") return "box_asleep";
  }
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

// ─── Connect in place (engines T2a) ──────────────────────────────────────────
// engine-connect-routes.handler.ts. The connect itself runs on the person's
// computer and reports its phases over the socket (ENGINE_CONNECT_PROGRESS);
// these two doors only start it and stop it. Every plan engine has its own
// (engines T3): /api/engines/<engine>/connect.

const enginePath = (engine: PlanEngine) => `/api/engines/${engine}`;
const connectPath = (engine: PlanEngine) => `${enginePath(engine)}/connect`;

/** How a connect signs in: the account slot, only when one is named (an add
 *  or a Reconnect; none is today's one account), and the way, only when it is
 *  not the browser (absent means the browser on the wire). */
export interface ConnectHow {
  slotId?: string;
  method?: EngineSignInMethod;
}

const connectBody = (connectId: string, { slotId, method }: ConnectHow = {}) =>
  JSON.stringify({
    connectId,
    ...(slotId ? { slotId } : {}),
    ...(method && method !== "browser" ? { method } : {}),
  });

export type ConnectStart =
  { outcome: "started"; awake: boolean } | { outcome: "no_credits" };

/**
 * POST the connect. 202 = the work continues on its own (`awake` says whether
 * the computer had to be woken); 402 no_credits is a STATE the row reports,
 * read from the structured field. Anything else throws.
 */
export async function connectEngine(
  engine: PlanEngine,
  connectId: string,
  how: ConnectHow = {}
): Promise<ConnectStart> {
  const res = await fetch(apiUrl(connectPath(engine)), {
    method: "POST",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    signal: deadline(),
    body: connectBody(connectId, how),
  });
  if (res.status === 202) {
    const body = (await res.json().catch(() => ({}))) as { awake?: unknown };
    return { outcome: "started", awake: body.awake === true };
  }
  if (res.status === 402) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === "no_credits") return { outcome: "no_credits" };
  }
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

/**
 * POST the cancel for ONE connect. 409 box_asleep means there was nothing to
 * cancel, which the row treats like a cancel that landed.
 */
export async function cancelEngineConnect(
  engine: PlanEngine,
  connectId: string,
  slotId?: string
): Promise<"sent" | "box_asleep"> {
  const res = await fetch(apiUrl(`${connectPath(engine)}/cancel`), {
    method: "POST",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    signal: deadline(),
    body: connectBody(connectId, { slotId }),
  });
  if (res.status === 202) return "sent";
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === "box_asleep") return "box_asleep";
  }
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

/**
 * The same cancel for a page that is going away (pagehide). Synchronous, with
 * a bearer read earlier, because the handler cannot await a token; `keepalive`
 * lets the request outlive the document, and unlike sendBeacon it carries the
 * header. Fire and forget: a stale token only earns a 401, and the box's own
 * ten-minute deadline is the floor.
 */
export function cancelEngineConnectOnLeave(
  engine: PlanEngine,
  connectId: string,
  token: string,
  slotId?: string
): void {
  try {
    void fetch(apiUrl(`${connectPath(engine)}/cancel`), {
      method: "POST",
      keepalive: true,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: connectBody(connectId, { slotId }),
    }).catch(() => undefined);
  } catch {
    // A page on its way out has nothing to do with a failure.
  }
}

// ─── Where the plan stands, fresh (engines T2b) ──────────────────────────────
// engine-connect-routes.handler.ts. The box reads the standing off the engine
// itself (no task) and reports it; the reading lands in engine_accounts, which
// the GET above returns. This door only ASKS, and only an awake computer is
// asked: a sleeping one answers box_asleep and is never woken.

export type StandingRefresh = "sent" | "box_asleep" | "too_soon";

/**
 * POST the ask. 202 = the awake computer is reading; 409 box_asleep and 429
 * too_soon are STATES (the last reading stays, with its age), read from the
 * structured field. Anything else throws.
 */
export async function refreshEngineStanding(
  engine: PlanEngine
): Promise<StandingRefresh> {
  const res = await fetch(apiUrl(`${enginePath(engine)}/standing/refresh`), {
    method: "POST",
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.status === 202) return "sent";
  if (res.status === 409 || res.status === 429) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    if (body.error === "box_asleep") return "box_asleep";
    if (body.error === "too_soon") return "too_soon";
  }
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

// ─── Your accounts of one plan (several accounts, spec 2026-10-01) ───────────
// engine-accounts-routes.handler.ts. Add mints the slot the in-place connect
// then signs in; Remove, "Use this one now" and the order answer the fresh
// Settings body, so the list is always drawn from the server's order. Each
// refusal the routes name is a STATE, read from the structured field; anything
// else throws. None of them wakes the computer.

const accountsPath = (engine: PlanEngine) => `${enginePath(engine)}/accounts`;
const slotPath = (engine: PlanEngine, slotId: string) =>
  `${accountsPath(engine)}/${encodeURIComponent(slotId)}`;

async function errorOf(res: Response): Promise<unknown> {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  return body.error;
}

export type AddAccount =
  { outcome: "added"; slotId: string } | { outcome: "too_many_accounts" };

/** POST the add: the server mints the slot (or hands back a free primary). */
export async function addAccount(engine: PlanEngine): Promise<AddAccount> {
  const res = await fetch(apiUrl(accountsPath(engine)), {
    method: "POST",
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as { slotId?: unknown };
    if (typeof body.slotId === "string" && body.slotId.length > 0)
      return { outcome: "added", slotId: body.slotId };
    throw new Error(`${res.status} no slotId`);
  }
  if (res.status === 409 && (await errorOf(res)) === "too_many_accounts")
    return { outcome: "too_many_accounts" };
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

export type AccountChange =
  { outcome: "done"; settings: EngineBody } | { outcome: "not_found" };

export type AccountRemoval =
  | { outcome: "removed"; settings: EngineBody }
  | { outcome: "not_found" }
  | { outcome: "last_account" };

/** DELETE one account. 404 = already gone; 409 last_account = the only one connected. */
export async function removeAccount(
  engine: PlanEngine,
  slotId: string
): Promise<AccountRemoval> {
  const res = await fetch(apiUrl(slotPath(engine, slotId)), {
    method: "DELETE",
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.ok)
    return { outcome: "removed", settings: (await res.json()) as EngineBody };
  if (res.status === 404) return { outcome: "not_found" };
  if (res.status === 409 && (await errorOf(res)) === "last_account")
    return { outcome: "last_account" };
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

/**
 * The same DELETE for a page that is going away inside a Remove's Undo window
 * (pagehide): synchronous, with a bearer read earlier, and `keepalive` so it
 * outlives the document, as cancelEngineConnectOnLeave. Fire and forget.
 */
export function removeAccountOnLeave(
  engine: PlanEngine,
  slotId: string,
  token: string
): void {
  try {
    void fetch(apiUrl(slotPath(engine, slotId)), {
      method: "DELETE",
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    }).catch(() => undefined);
  } catch {
    // A page on its way out has nothing to do with a failure.
  }
}

/**
 * "Use this one now": the account is pinned, and used while it has room; once
 * it runs out, the person's order choice carries on. Not named use… on
 * purpose: React reads a use-prefixed function as a hook.
 */
export async function chooseAccountNow(
  engine: PlanEngine,
  slotId: string
): Promise<AccountChange> {
  const res = await fetch(apiUrl(`${slotPath(engine, slotId)}/use`), {
    method: "POST",
    headers: await authHeader(),
    credentials: "include",
    signal: deadline(),
  });
  if (res.ok)
    return { outcome: "done", settings: (await res.json()) as EngineBody };
  if (res.status === 404) return { outcome: "not_found" };
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

export type AccountOrder =
  { outcome: "done"; settings: EngineBody } | { outcome: "mismatch" };

/** PATCH the whole order. 400 mismatch = the list was stale (an add or a removal landed). */
export async function reorderAccounts(
  engine: PlanEngine,
  slotIds: string[]
): Promise<AccountOrder> {
  const res = await fetch(apiUrl(`${accountsPath(engine)}/order`), {
    method: "PATCH",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    signal: deadline(),
    body: JSON.stringify({ slotIds }),
  });
  if (res.ok)
    return { outcome: "done", settings: (await res.json()) as EngineBody };
  if (res.status === 400 && (await errorOf(res)) === "mismatch")
    return { outcome: "mismatch" };
  throw new Error(`${res.status} ${await responseSnippet(res)}`);
}
